import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, User, Phone, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SchedulerCalendar = () => {
  const { getAuthHeader } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [consultations, setConsultations] = useState([]);
  const [followUps, setFollowUps] = useState([]); // Lead follow-up notifications
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConsultations();
    fetchFollowUps();
  }, [currentDate]); // Refetch when month changes

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/admin/consultations`,
        { headers: getAuthHeader() }
      );
      setConsultations(response.data);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowUps = async () => {
    try {
      // Fetch all scheduled notifications for the current month
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
      
      const response = await axios.get(
        `${BACKEND_URL}/api/scheduled-notifications/month?year=${year}&month=${month}`,
        { headers: getAuthHeader() }
      );
      
      setFollowUps(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch follow-ups:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const getConsultationsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return consultations.filter(c => c.callback_date === dateStr);
  };

  const getFollowUpsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return followUps.filter(f => {
      const scheduledDate = new Date(f.scheduled_time);
      // Convert to IST for date comparison
      const istDateStr = scheduledDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // en-CA gives YYYY-MM-DD format
      return istDateStr === dateStr;
    });
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const handleDateClick = (day) => {
    const dateConsultations = getConsultationsForDate(day);
    const dateFollowUps = getFollowUpsForDate(day);
    if (dateConsultations.length > 0 || dateFollowUps.length > 0) {
      setSelectedDate({ day, consultations: dateConsultations, followUps: dateFollowUps });
    }
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading scheduler...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Event Scheduler</h1>
          <p className="body-large">View consultations and lead follow-ups</p>
        </div>
      </div>

      <div className="scheduler-container">
        {/* Calendar Header */}
        <div className="calendar-header">
          <button onClick={previousMonth} className="btn-secondary calendar-nav-btn">
            <ChevronLeft size={20} />
          </button>
          <h2 className="heading-2">{monthName}</h2>
          <button onClick={nextMonth} className="btn-secondary calendar-nav-btn">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="calendar-wrapper">
          <div className="calendar-grid">
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="calendar-day-header">
                {day}
              </div>
            ))}

            {/* Empty cells for days before month starts */}
            {[...Array(startingDayOfWeek)].map((_, i) => (
              <div key={`empty-${i}`} className="calendar-day empty"></div>
            ))}

            {/* Days of the month */}
            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1;
              const dayConsultations = getConsultationsForDate(day);
              const dayFollowUps = getFollowUpsForDate(day);
              const totalEvents = dayConsultations.length + dayFollowUps.length;
              const hasEvents = totalEvents > 0;

              return (
                <div
                  key={day}
                  onClick={() => handleDateClick(day)}
                  className={`calendar-day ${hasEvents ? 'has-consultations' : ''} ${isToday(day) ? 'today' : ''}`}
                  style={{ 
                    cursor: hasEvents ? 'pointer' : 'default',
                    minHeight: '80px',
                    padding: '4px'
                  }}
                >
                  <div className="day-number">{day}</div>
                  
                  {/* Display follow-up events directly on calendar (Google Calendar style) */}
                  {dayFollowUps.slice(0, 3).map((followUp, idx) => {
                    const scheduledDate = new Date(followUp.scheduled_time);
                    const timeStr = scheduledDate.toLocaleTimeString('en-IN', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: false,
                      timeZone: 'Asia/Kolkata'
                    });
                    
                    const getStatusColor = (status) => {
                      switch (status) {
                        case 'completed': return '#10b981'; // Green
                        case 'missed': return '#ef4444'; // Red
                        case 'pending': return '#f59e0b'; // Orange
                        case 'snoozed': return '#a855f7'; // Purple
                        default: return '#9ca3af'; // Gray
                      }
                    };

                    return (
                      <div
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/admin/leads?lead_id=${followUp.lead_id}&openUpdate=true`;
                        }}
                        style={{
                          fontSize: '10px',
                          padding: '2px 4px',
                          marginTop: '2px',
                          background: getStatusColor(followUp.status),
                          color: 'white',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: '600'
                        }}
                        title={`${followUp.lead_id} - ${followUp.lead_name} (${followUp.status})`}
                      >
                        {timeStr} {followUp.lead_id}
                      </div>
                    );
                  })}
                  
                  {dayFollowUps.length > 3 && (
                    <div style={{
                      fontSize: '9px',
                      color: '#6b7280',
                      marginTop: '2px',
                      fontWeight: '600'
                    }}>
                      +{dayFollowUps.length - 3} more
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Date Details */}
        {selectedDate && (
          <div className="selected-date-panel">
            <div className="panel-header">
              <h3 className="heading-3">
                Events on {selectedDate.day} {monthName}
              </h3>
              <button onClick={() => setSelectedDate(null)} className="btn-secondary">
                Close
              </button>
            </div>

            {/* Lead Follow-Ups Section */}
            {selectedDate.followUps && selectedDate.followUps.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#f59e0b',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  📞 Lead Follow-Ups ({selectedDate.followUps.length})
                </h4>
                <div className="consultations-list">
                  {selectedDate.followUps.map(followUp => {
                    const scheduledDate = new Date(followUp.scheduled_time);
                    const timeStr = scheduledDate.toLocaleTimeString('en-IN', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: true,
                      timeZone: 'Asia/Kolkata'
                    });
                    
                    const getStatusColor = (status) => {
                      switch (status) {
                        case 'missed': return '#ef4444';
                        case 'pending': return '#f59e0b';
                        case 'snoozed': return '#a855f7';
                        default: return '#9ca3af';
                      }
                    };

                    return (
                      <div 
                        key={followUp.id} 
                        className="consultation-card-scheduler"
                        style={{
                          borderLeft: `4px solid ${getStatusColor(followUp.status)}`,
                          cursor: 'pointer'
                        }}
                        onClick={() => window.location.href = `/admin/leads?lead_id=${followUp.lead_id}&openUpdate=true`}
                      >
                        <div className="consultation-time">
                          <Clock size={18} color={getStatusColor(followUp.status)} />
                          <span className="time-text">{timeStr}</span>
                          <span style={{
                            marginLeft: '8px',
                            fontSize: '11px',
                            fontWeight: '700',
                            color: 'white',
                            background: getStatusColor(followUp.status),
                            padding: '2px 8px',
                            borderRadius: '4px'
                          }}>
                            {followUp.status.toUpperCase()}
                          </span>
                        </div>
                        
                        <div className="consultation-info">
                          <div className="info-row">
                            <User size={16} />
                            <span><strong>{followUp.lead_name}</strong></span>
                          </div>
                          {followUp.lead_phone && (
                            <div className="info-row">
                              <Phone size={16} />
                              <span>{followUp.lead_phone}</span>
                            </div>
                          )}
                          <div className="info-row" style={{ 
                            fontStyle: 'italic', 
                            color: '#6b7280',
                            marginTop: '8px',
                            paddingTop: '8px',
                            borderTop: '1px solid #e5e7eb'
                          }}>
                            <span>"{followUp.remarks}"</span>
                          </div>
                          <div style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            Assigned to: {followUp.user_id}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Consultations Section */}
            {selectedDate.consultations && selectedDate.consultations.length > 0 && (
              <div>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#3b82f6',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  📅 Consultations ({selectedDate.consultations.length})
                </h4>
                <div className="consultations-list">
                  {selectedDate.consultations.map(consultation => (
                    <div key={consultation.id} className="consultation-card-scheduler">
                      <div className="consultation-time">
                        <Clock size={18} />
                        <span className="time-text">{consultation.callback_time}</span>
                      </div>
                      
                      <div className="consultation-info">
                        <div className="info-row">
                          <User size={16} />
                          <span><strong>{consultation.name}</strong></span>
                        </div>
                        <div className="info-row">
                          <Phone size={16} />
                          <span>{consultation.phone}</span>
                        </div>
                        <div className="info-row">
                          <Mail size={16} />
                          <span>{consultation.email}</span>
                        </div>
                        <div className="info-row">
                          <CalendarIcon size={16} />
                          <span>{consultation.visa_type} - {consultation.country}</span>
                        </div>
                      </div>

                      <div className={`consultation-status status-${consultation.status}`}>
                        {consultation.status.replace('_', ' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchedulerCalendar;
